import {activateAccountResolver, existingWorkspacesResolver, logoutResolver, oauthSocialCallbackResolver} from '@domains/auth/resolvers'
import {type NavigationGuardNext, type RouteLocationNormalized} from 'vue-router'
import {confirmEmailResolver} from '@domains/auth/resolvers/confirm-email.resolver'
import AuthenticationView from '@domains/auth/views/authentation.view.vue'
import {confirmDeleteWorkspaceResolver} from '@domains/account/resolvers/confirm-delete-workspace.resolver'
import {confirmDeleteAccountResolver} from '@domains/account/resolvers/confirm-delete-account.resolver'
import {confirmInvitationResolver} from '@domains/auth/resolvers/confirm-invitation.resolver'
import {AuthenticationNs} from "@shared/types";

export const authenticationRoutes = [
    {
        path: '/auth',
        name: AuthenticationNs.AuthenticationRouteEnums.Auth,
        component: AuthenticationView,
        redirect: {name: AuthenticationNs.AuthenticationRouteEnums.SignIn},
        children: [
            {
                path: 'sign-in',
                name: AuthenticationNs.AuthenticationRouteEnums.SignIn,
                component: () => import('@domains/auth/widgets/sign-in-form.widget.vue'),
                meta: {
                    requiresAuth: false,
                    data: {
                        title: 'Sign in to metastory',
                        subtitle: 'Welcome back.',
                        routeTo: 'signUp'
                    }
                }
            },
            {
                path: 'social',
                name: AuthenticationNs.AuthenticationRouteEnums.Social,
                component: () => import('@shared/components/widgets/dummy.widget.vue'),
                meta: {
                    requiresAuth: false,
                },
                beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                    oauthSocialCallbackResolver(to, from, next)
                }
            },
            {
                path: 'forgot-password',
                name: AuthenticationNs.AuthenticationRouteEnums.ForgotPassword,
                component: () => import('@domains/auth/widgets/forgot-password-form.widget.vue'),
                meta: {
                    requiresAuth: false,
                    data: {
                        title: 'Forgot password?',
                        subtitle: 'Enter an email address to reset the password.',
                        routeTo: 'signUp'
                    }
                }
            },
            {
                path: 'reset-password',
                name: AuthenticationNs.AuthenticationRouteEnums.NewPassword,
                component: () => import('@domains/auth/widgets/new-password-form.widget.vue'),
                meta: {
                    requiresAuth: false,
                    data: {
                        title: 'Choose a new password',
                        subtitle: 'A few words that are easy to remember.',
                        routeTo: 'signIn'
                    }
                }
            },
            {
                path: 'logout',
                name: AuthenticationNs.AuthenticationRouteEnums.Logout,
                component: () => import('@shared/components/widgets/dummy.widget.vue'),
                meta: {
                    requiresAuth: false
                },
                beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                    logoutResolver(to, from, next)
                }
            },
            {
                path: 'confirm-email',
                name: AuthenticationNs.AuthenticationRouteEnums.ConfirmEmail,
                component: () => import('@shared/components/widgets/dummy.widget.vue'),
                meta: {
                    requiresAuth: false
                },
                beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                    confirmEmailResolver(to, from, next)
                }
            },
            {
                path: 'recovery-link',
                name: AuthenticationNs.AuthenticationRouteEnums.RecoveryLink,
                component: () => import('@domains/auth/widgets/check-email.widget.vue'),
                meta: {
                    requiresAuth: false,
                    data: {
                        title: 'Recovery link sent.',
                        subtitle: 'Check the inbox for reset instructions.',
                        routeTo: 'signIn'
                    }
                }
            },
            {
                path: 'existing-workspaces',
                name: AuthenticationNs.AuthenticationRouteEnums.ExistingWorkspaces,
                beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                    existingWorkspacesResolver(to, from, next)
                },
                component: () => import('@domains/auth/widgets/existing-workspaces.widget.vue'),
                meta: {
                    data: {
                        title: 'Log in to existing workspace',
                        subtitle: 'Already a member of the following workspaces.'
                    }
                }
            },
            {
                path: 'existing-create-workspaces',
                name: AuthenticationNs.AuthenticationRouteEnums.ExistingCreateWorkspaces,
                component: () => import('@domains/auth/widgets/existing-workspaces.widget.vue'),
                meta: {
                    data: {
                        title: 'Log in to existing or create a new workspace',
                        subtitle: 'Already a member of the following workspaces.'
                    }
                }
            },
            {
                path: 'confirm-delete-workspace',
                name: AuthenticationNs.AuthenticationRouteEnums.ConfirmDeleteWorkspace,
                component: () => import('@shared/components/widgets/dummy.widget.vue'),
                meta: {
                    requiresAuth: false
                },
                beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                    confirmDeleteWorkspaceResolver(to, from, next)
                }
            },
            {
                path: 'confirm-delete-account',
                name: AuthenticationNs.AuthenticationRouteEnums.ConfirmDeleteAccount,
                component: () => import('@shared/components/widgets/dummy.widget.vue'),
                meta: {
                    requiresAuth: false
                },
                beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                    confirmDeleteAccountResolver(to, from, next)
                }
            },
            {
                path: 'sign-up',
                name: 'RegisterView',
                component: () => import('@domains/auth/views/registration.view.vue'),
                meta: {
                    requiresAuth: false
                },
                redirect: {name: AuthenticationNs.AuthenticationRouteEnums.SignUp},
                children: [
                    {
                        path: '',
                        name: AuthenticationNs.AuthenticationRouteEnums.SignUp,
                        component: () => import('@domains/auth/widgets/sign-up-form.widget.vue'),
                        meta: {
                            requiresAuth: false,
                            data: {
                                title: 'Sign up for metastory',
                                subtitle: 'Transform the world with a digital product.',
                                routeTo: 'signIn'
                            }
                        }
                    },
                    {
                        path: 'activate-account',
                        name: 'ActivateAccount',
                        component: () => import('@shared/components/widgets/dummy.widget.vue'),
                        meta: {
                            requiresAuth: false
                        },
                        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                            activateAccountResolver(to, from, next)
                        }
                    },
                    {
                        path: 'check-email',
                        name: 'CheckEmail',
                        component: () => import('@domains/auth/widgets/check-email.widget.vue'),
                        meta: {
                            requiresAuth: false,
                            data: {
                                title: 'Check the email',
                                subtitle: 'If not received immediately, check the spam folder. Confirm the email address to start the onboarding.',
                                routeTo: 'signIn'
                            }
                        }
                    },
                    {
                        path: 'lets-start',
                        name: 'LetsStart',
                        component: () => import('@domains/auth/widgets/lets-start-form.widget.vue'),
                        meta: {
                            requiresAuth: false,
                            data: {
                                title: 'Getting started',
                                subtitle: 'Enter personal information.',
                                step: 'step-1'
                            }
                        }
                    },
                    {
                        path: 'create-workspace',
                        name: 'CreateWorkspace',
                        component: () => import('@domains/auth/widgets/workspace-name-form.widget.vue'),
                        meta: {
                            requiresAuth: false,
                            data: {
                                title: 'Name the workspace',
                                subtitle: 'Choose a name such as a team or company.',
                                step: 'step-2'
                            }
                        }
                    },
                    {
                        path: 'invite-teammate',
                        name: 'InviteTeammate',
                        component: () => import('@domains/auth/widgets/invite-teammate-form.widget.vue'),
                        meta: {
                            requiresAuth: false,
                            data: {
                                title: 'Invite a teammate',
                                subtitle: 'Add team members to the workspace.',
                                step: 'step-3'
                            }
                        }
                    },
                    {
                        path: 'confirm-invitation',
                        name: 'ConfirmInvitation',
                        component: () => import('@shared/components/widgets/dummy.widget.vue'),
                        meta: {
                            requiresAuth: false
                        },
                        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
                            confirmInvitationResolver(to, from, next)
                        }
                    },
                    {
                        path: 'welcome',
                        name: 'Welcome',
                        component: () => import('@domains/auth/widgets/welcome-form.widget.vue'),
                        meta: {
                            requiresAuth: false,
                            data: {
                                title: 'Welcome to metastory!',
                                subtitle: 'One last step.',
                                step: 'step-4'
                            }
                        }
                    }
                ]
            }
        ]
    },
    {
        path: '/loading',
        name: 'Loading',
        component: () => import('@shared/components/widgets/app-loading.widget.vue'),
    }

]
